import { motion } from 'framer-motion'
import { X } from 'lucide-react'
import { useId, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { BundleDetailLineSocial } from '@/Components/custom/bundle/detail-list'
import { LazySvg } from '@/Components/custom/lazy-svg'
import { Badge } from '@/Components/ui/badge'
import { Button } from '@/Components/ui/button'
import { MOTION, outgoing } from '@/Components/ui/morph-card'
import { useHydrated } from '@/hooks/use-hydrated'
import { useLocalizedBundle } from '@/hooks/use-localized-value'
import { useSafeTranslation } from '@/hooks/use-safe-translation'
import { adjustFont, cn, formatUssdString } from '@/lib/utils'
import { storeLink } from '@/lib/vip-card'
import type { Bundle } from '@/types'
import { CardArrow } from './card-arrow'

/**
 * What a plan is drawn in. It takes the page's own surface and text, so it
 * follows the theme like everything else on the board.
 */
const TONE = {
  strong: 'text-foreground',
  soft: 'text-muted-foreground',
  frame: 'border-border',
}

export const PLAN_SURFACE = 'bg-card text-foreground'

export const PLAN_SHEET =
  'vip-theme rounded-t-[24px] border-0 bg-card text-foreground [&>div:first-of-type]:mb-1 [&>div:first-of-type]:mt-3 [&>div:first-of-type]:h-1 [&>div:first-of-type]:w-10 [&>div:first-of-type]:bg-[hsl(var(--muted-foreground)/0.3)]'

interface PlanLine {
  id: string
  category: string
  value: string
  is_unlimited: boolean
  show_as_subtitle: boolean
  order: number
  settings: { icon: string; name: string }
  regions?: unknown
}

export interface PlanFacts {
  title: string
  titleSize: string
  validity: string
  price: number
  /** The flag of the country a roaming plan is for. */
  country: string | null
  lines: PlanLine[]
  badges: PlanLine[]
  rates: { legend: string; items: PlanLine[] } | null
  regions: { legend: string; names: string[] } | null
  /** False for a plan that is shown but cannot be taken up from anywhere. */
  canActivate: boolean
}

const RATES = ['onnet_minutes_rate', 'offnet_minutes_rate', 'onnet_sms_rate', 'offnet_sms_rate']

/** Drawn as their own blocks, so they are not lines. */
const BLOCKS = ['country', 'regions', 'rate_note', ...RATES]

/** Everything a plan shows, resolved once and synchronously. */
export function usePlanFacts(plan: Bundle): PlanFacts {
  const localized = useLocalizedBundle(plan)
  const { i18n } = useTranslation()
  const language = useHydrated() ? i18n.language : 'en'

  const inLanguage = (value: unknown): string => {
    if (typeof value === 'string') {
      return value
    }

    const translated = value as Record<string, string> | null | undefined

    return translated?.[language] || translated?.en || ''
  }

  const title = String(localized.title ?? '')
  const all = ((localized.attributes ?? []) as unknown as PlanLine[])
    .map((line) => ({
      ...line,
      settings: { ...line.settings, name: inLanguage(line.settings?.name) },
    }))
    .sort((a, b) => (a.order || 0) - (b.order || 0))

  const legend = all.find((line) => line.category === 'rate_note')?.value
  const rateItems = all.filter((line) => RATES.includes(line.category) && line.value)

  const regionsLine = all.find((line) => line.category === 'regions')
  const regionNames = Object.values((regionsLine?.regions ?? {}) as Record<string, unknown>)
    .map(inLanguage)
    .filter(Boolean)

  return {
    title,
    titleSize: adjustFont(title),
    validity: String(localized.validity ?? ''),
    price: Number(plan.price),
    country: all.find((line) => line.category === 'country')?.settings.icon || null,
    lines: all.filter((line) => !BLOCKS.includes(line.category) && line.value),
    badges: all.filter((line) => line.show_as_subtitle && line.value),
    rates: legend && rateItems.length ? { legend, items: rateItems } : null,
    regions:
      regionsLine && regionNames.length
        ? { legend: regionsLine.settings.name, names: regionNames }
        : null,
    canActivate: plan.monetization !== 'no',
  }
}

export const PLAN_TITLE = 'block pe-8 font-bold leading-none tracking-tight'

/**
 * The shape behind a resting plan, as the board draws it.
 *
 * Its two bands fade into the card, so what they fade from and to follows
 * the theme. It spans the card and thins to nothing towards the leading side,
 * and is mirrored with the writing direction.
 */
export function PlanBackdrop() {
  const id = useId().replace(/:/g, '')

  return (
    <svg
      aria-hidden
      preserveAspectRatio="none"
      viewBox="0 0 298.193 142.259"
      fill="none"
      className="absolute inset-0 size-full [--shape-end:#fff] [--shape-gray:#D1D1D6] [--shape-red:#FFD6D6] [mask-image:linear-gradient(to_left,black_25%,transparent_92%)] dark:[--shape-end:#000] dark:[--shape-gray:#636366] dark:[--shape-red:#3D0000] rtl:-scale-x-100"
    >
      <path
        d="M194.947 42.6948C120.51 71.4588 75.6018 73.0553 75.6018 73.0553H75.5979C52.1406 74.8837 26.7747 73.1158 0 65.8424V142.259H298.189V25.8795C298.189 25.8795 269.384 13.9309 194.947 42.6948Z"
        fill={`url(#${id}-red)`}
      />
      <path
        d="M233.464 0.0537769C233.464 0.0537769 169.97 65.6885 75.6019 73.0559H75.6058C75.6058 73.0559 120.514 71.4628 194.951 42.6989C269.388 13.9349 298.193 25.8835 298.193 25.8835V0.0537769C298.193 0.0537769 233.468 -0.0672212 233.468 0.0537769H233.464Z"
        fill={`url(#${id}-gray)`}
      />
      <defs>
        <linearGradient
          id={`${id}-red`}
          x1="238.466"
          y1="-456.378"
          x2="160.995"
          y2="94.1601"
          gradientUnits="userSpaceOnUse"
        >
          <stop offset="0.742639" style={{ stopColor: 'var(--shape-red)' }} />
          <stop offset="0.993721" style={{ stopColor: 'var(--shape-end)' }} />
        </linearGradient>
        <linearGradient
          id={`${id}-gray`}
          x1="254.921"
          y1="-131.019"
          x2="302.268"
          y2="12.5156"
          gradientUnits="userSpaceOnUse"
        >
          <stop style={{ stopColor: 'var(--shape-gray)' }} />
          <stop offset="1" style={{ stopColor: 'var(--shape-end)' }} />
        </linearGradient>
      </defs>
    </svg>
  )
}

/** The flag on the resting card. */
export function PlanFlag({ facts }: { facts: PlanFacts }) {
  return facts.country ? (
    <img
      src={facts.country}
      alt=""
      className={cn('size-10 rounded-full border object-contain', TONE.frame)}
    />
  ) : null
}

/** The same flag behind the open plan's title, large and faint. */
export function PlanFlagMark({ facts }: { facts: PlanFacts }) {
  return facts.country ? (
    <img
      src={facts.country}
      alt=""
      aria-hidden
      className="h-24 rounded-xl object-contain opacity-20 blur-[2px]"
    />
  ) : null
}

function Unlimited({ className }: { className?: string }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      className={cn('shrink-0 text-primary', className)}
      viewBox="0 0 22 11"
    >
      <path
        stroke="currentColor"
        strokeMiterlimit="10"
        strokeWidth="1.6"
        d="M2.30939 8.65889c1.7468 1.74681 4.57938 1.74681 6.32618 0l3.16263-3.16261-3.16263-3.16262C6.88877.58686 4.05619.58686 2.30939 2.3327c-1.74585 1.74776-1.74585 4.57939 0 6.32619ZM19.8836 8.1911c1.4885-1.48852 1.4885-3.90209 0-5.38965-1.4885-1.48851-3.9021-1.48851-5.3896 0l-2.6949 2.69483L14.494 8.1911c1.4885 1.48851 3.9021 1.48851 5.3896 0Z"
      />
    </svg>
  )
}

/**
 * What the resting card says under its name: the first three, one to a line,
 * each cut where the card ends. The price keeps the foot of the card that way,
 * and the open plan carries every line in full.
 */
export function PlanBadges({ facts }: { facts: PlanFacts }) {
  return (
    <div className="mt-2 flex flex-col gap-1">
      {facts.badges.slice(0, 3).map((badge) => (
        <span
          key={badge.category}
          className={cn('flex h-5 min-w-0 items-center gap-1 text-xs font-medium', TONE.strong)}
        >
          {badge.settings.icon ? (
            <LazySvg
              name={badge.settings.icon}
              containsPath={true}
              className="size-3 shrink-0 text-primary"
            />
          ) : null}
          <span className="min-w-0 truncate">{badge.value}</span>
          {badge.is_unlimited ? <Unlimited className="size-4" /> : null}
        </span>
      ))}
    </div>
  )
}

export function Currency({ className }: { className?: string }) {
  return <LazySvg name="/icons/currency-alt.svg" className={cn('size-5 shrink-0', className)} />
}

/** The arrow the resting card carries, as the partner cards do. */
export function PlanArrow() {
  return <CardArrow />
}

function Clock() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className="size-4 text-primary"
      fill="none"
      viewBox="0 0 18 18"
    >
      <path
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth="1.2"
        d="M8.99998 5.30971v3.69026l3.39162 3.39163M17 9c0 4.4183-3.5817 8-8 8-4.41828 0-8-3.5817-8-8 0-4.41828 3.58172-8 8-8 4.4183 0 8 3.58172 8 8Z"
      />
    </svg>
  )
}

/**
 * One row of the open plan: an icon, what it says, and what closes the row.
 *
 * Every row is the same three columns on the same line height, so the icons
 * share an edge, the text shares an edge, and a row is as tall as its text --
 * never as tall as whatever sits at its end.
 */
function Row({ icon, end, children }: { icon?: ReactNode; end?: ReactNode; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[1rem_minmax(0,1fr)_auto] items-start gap-x-2.5 text-sm leading-5">
      <span className="flex h-5 items-center">{icon}</span>
      <span className="min-w-0">{children}</span>
      {end ? <span className="flex h-5 items-center">{end}</span> : null}
    </div>
  )
}

function LineIcon({ name }: { name?: string }) {
  return name ? <LazySvg name={name} containsPath={true} className="size-4 text-primary" /> : null
}

/** One thing the plan carries. */
function Line({ line }: { line: PlanLine }) {
  const label = line.settings?.name ? `${line.settings.name}: ` : null

  if (line.category === 'social_platforms' && !line.is_unlimited) {
    return (
      <ul>
        <BundleDetailLineSocial />
      </ul>
    )
  }

  if (line.category === 'libraries') {
    return (
      <Row icon={<LineIcon name={line.settings.icon} />}>
        <span className="flex flex-wrap items-center gap-1">
          {label ? <span className={TONE.soft}>{label}</span> : null}
          {line.value.split(',').map((item) => (
            <Badge key={item} variant="destructive-outline">
              {item.trim()}
            </Badge>
          ))}
        </span>
      </Row>
    )
  }

  return (
    <Row
      icon={<LineIcon name={line.settings.icon} />}
      end={line.is_unlimited ? <Unlimited className="size-5" /> : null}
    >
      {label ? <span className={TONE.soft}>{label}</span> : null}
      <span className={TONE.strong}>
        {line.category === 'ussd' ? formatUssdString(line.value) : line.value}
      </span>
    </Row>
  )
}

/**
 * What the plan carries, line by line.
 *
 * They arrive a step apart once the card's own line has gone, the step
 * shortened so a long plan still finishes with the flight. They leave together.
 */
export function PlanLines({
  facts,
  shown,
  duration,
}: {
  facts: PlanFacts
  shown: boolean
  /** The flight open; the flight shut is derived from it. */
  duration: number
}) {
  const leave = duration * MOTION.exit
  const count = facts.lines.length + 1
  const step = Math.min(0.06, 0.28 / Math.max(1, count - 1))
  const arrives = (index: number) => outgoing(duration) + duration * (0.05 + index * step)

  const row = (index: number) =>
    shown
      ? {
          opacity: 1,
          y: 0,
          transition: {
            opacity: { duration: duration * 0.3, delay: arrives(index), ease: 'linear' },
            y: { duration: duration * 0.45, delay: arrives(index), ease: MOTION.ease.decelerate },
          },
        }
      : { opacity: 0, y: 0, transition: { duration: outgoing(leave), ease: 'linear' } }

  return (
    <div className="flex flex-col gap-2.5">
      <motion.div data-plan-line initial={{ opacity: 0, y: 10 }} animate={row(0)}>
        <Row icon={<Clock />}>
          <span className={TONE.strong}>{facts.validity}</span>
        </Row>
      </motion.div>

      {facts.lines.map((line, index) => (
        <motion.div
          key={line.id}
          data-plan-line
          initial={{ opacity: 0, y: 10 }}
          animate={row(index + 1)}
        >
          <Line line={line} />
        </motion.div>
      ))}
    </div>
  )
}

/**
 * A titled block under the lines.
 *
 * Its frame sits outside the column the lines keep, so what is inside it lines
 * up with them instead of being pushed in by its own padding.
 */
function Block({ legend, children }: { legend: string; children: ReactNode }) {
  return (
    <fieldset className={cn('-mx-2.5 rounded-[12px] border px-[9px] pb-2.5 pt-1', TONE.frame)}>
      <legend className={cn('-ms-1 px-1 text-xs font-medium leading-5', TONE.soft)}>
        {legend}
      </legend>
      {children}
    </fieldset>
  )
}

export function PlanRates({ facts }: { facts: PlanFacts }) {
  if (!facts.rates) {
    return null
  }

  return (
    <Block legend={facts.rates.legend}>
      <div className="flex flex-col gap-2">
        {facts.rates.items.map((rate) => (
          <Row
            key={rate.id}
            icon={<LineIcon name={rate.settings.icon} />}
            end={
              <span className="inline-flex items-center tabular-nums">
                <Currency className="size-3.5 text-primary" />
                <span className={cn('font-medium', TONE.strong)}>{rate.value}</span>
              </span>
            }
          >
            <span className={TONE.soft}>{rate.settings.name}</span>
          </Row>
        ))}
      </div>
    </Block>
  )
}

export function PlanRegions({ facts }: { facts: PlanFacts }) {
  if (!facts.regions) {
    return null
  }

  return (
    <Block legend={facts.regions.legend}>
      <div className="flex flex-wrap gap-1.5 pt-1">
        {facts.regions.names.map((name) => (
          <Badge key={name} variant="destructive" className="rounded-sm px-1">
            {name}
          </Badge>
        ))}
      </div>
    </Block>
  )
}

/** The site activates nothing for these: the button sends the subscriber to the app. */
export function PlanActivate({ facts }: { facts: PlanFacts }) {
  const t = useSafeTranslation()

  if (!facts.canActivate) {
    return null
  }

  return (
    <Button variant="destructive" asChild className="w-full rounded-[12px]">
      <a
        href={storeLink()}
        target="_blank"
        rel="noopener noreferrer"
        onClick={(event) => event.stopPropagation()}
        className="rtl:text-base rtl:font-medium"
      >
        {t('plan.activate')}
      </a>
    </Button>
  )
}

/** The button alone, for a card that places and moves it itself. */
export function PlanCloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      aria-label="Close"
      onClick={(event) => {
        event.stopPropagation()
        onClose()
      }}
      className={cn(
        'group/close flex size-7 items-center justify-center rounded-[12px] transition-colors',
        'text-foreground hover:bg-foreground/[0.06]'
      )}
    >
      <X className="size-5 transition-transform duration-300 group-hover/close:rotate-90" />
    </button>
  )
}

export const PLAN_CLOSE_SPOT = 'absolute end-3 top-3 z-20'

/**
 * The open plan's edge.
 *
 * A line along the top and the bottom only, strongest in the middle and gone
 * before it reaches a corner. The box has no border, only this.
 */
export function PlanEdge({ at }: { at: 'top' | 'bottom' }) {
  return (
    <span
      aria-hidden
      className={cn(
        'pointer-events-none absolute inset-x-6 h-px bg-gradient-to-r from-transparent via-primary to-transparent',
        at === 'top' ? 'top-0' : 'bottom-0'
      )}
    />
  )
}
